import React, { memo } from "react";

import type { Player } from "@/features/player-kanban/types/types";
import KanbanColumnContent from "@/features/player-kanban/components/organisms/KanbanColumnContent";
import KanbanColumnHeader from "@/features/player-kanban/components/molecules/KanbanColumnHeader";
import StageOptionsMenu from "@/features/player-kanban/components/molecules/StageOptionsMenu";

import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import CountBadge from "@/components/molecules/CountBadge";

import { TEST_ID_PLAYER_KANBAN } from "e2e/testId";

/**
 *
 */
type SortOption = "name" | "age" | "gpr" | "contract" | "rank";

/**
 * Props for KanbanColumnView component.
 * @remarks
 * This interface defines the view props that render the column UI.
 * All drag-and-drop interactions use typed DragData and DragEvent from the feature types module.
 */
interface KanbanColumnViewProps {
  /**
   * Column identifier (prefixed with underscore for internal use)
   */
  _columnId: string;
  /**
   * Column header title text
   */
  title: string;
  /**
   * Total number of players in column
   */
  count: number;
  /**
   * Array of sorted Player objects to render
   */
  players: Player[];
  /**
   * Whether column has no players
   */
  isEmpty: boolean;
  /**
   * Text message displayed when column is empty
   */
  emptyText: string;
  /**
   * Optional board identifier
   */
  boardId?: string;
  /**
   * Optional numeric trigger for board refresh
   */
  boardRefreshTrigger?: number;
  /**
   * Whether title is currently being edited
   */
  isEditingTitle: boolean;
  /**
   * Current value of title input field
   */
  editTitleValue: string;
  /**
   * Whether any drag operation is active across board
   */
  isGloballyDragging?: boolean;
  /**
   * Whether drag operation originated from this column
   */
  isDraggingFromThisColumn: boolean;
  /**
   * SE-6554 (Figma 80:16392): whether the dragged card is currently aimed at
   * this lane, i.e. this is the stage that would receive it on drop.
   */
  isDragOver?: boolean;
  /**
   * SE-6554: reports the droppable's `isOver` up from the content area, which
   * is where dnd-kit registers it. The outline it drives is drawn around the
   * whole column, header included, so the flag has to travel this far up.
   */
  onDragOverChange?: (isOver: boolean) => void;
  /**
   * Current sort criterion being applied
   */
  sortBy: SortOption;
  /**
   * Current sort direction
   */
  sortDirection: "asc" | "desc";
  /**
   * Whether user can edit column title
   */
  canEdit?: boolean;
  /**
   * Whether user can delete column
   */
  canDelete?: boolean;
  /**
   * SE-4685: Whether this column is sorted by Rank and editable, enabling the
   * within-column drag-to-reorder grab handle on each card.
   */
  isRankReorderEnabled?: boolean;
  /**
   * Callback when player card is clicked
   */
  onPlayerClick: (playerId: string) => void;
  /**
   * Callback when title edit mode starts
   */
  onStartTitleEdit: () => void;
  /**
   * Callback when edited title is saved
   */
  onSaveTitleEdit: () => void;
  /**
   * Callback when title editing is cancelled
   */
  onCancelTitleEdit: () => void;
  /**
   * Callback when title input value changes
   */
  onTitleChange: (value: string) => void;
  /**
   * Callback when player is dropped into column
   */
  onPlayerDrop?: (playerData: unknown) => void;
  /**
   * Callback when drag starts from this column
   */
  onLocalDragStart: (draggedData?: unknown, event?: unknown) => void;
  /**
   * Callback when drag from this column ends
   */
  onLocalDragEnd: () => void;
  /**
   * Callback to trigger horizontal auto-scrolling
   */
  onAutoScroll?: (dragX: number) => void;
  /**
   * Callback when sort option is selected
   */
  onSort: (option: SortOption) => void;
  /**
   * Callback when column deletion is requested
   */
  onDelete?: () => void;
  /**
   * Callback for tag addition (prefixed with underscore for internal use)
   */
  _onTagAdded?: (playerId: string, tag: string) => void;
  /**
   * Function to retrieve player tags (prefixed with underscore for internal use)
   */
  _getPlayerTags?: (playerId: string) => string[];
  /**
   * Callback when board refresh is requested
   */
  onBoardRefresh?: () => void;
}

/**
 * KanbanColumnView - Renders the visual structure of a Kanban column with header, content, and player cards
 * @param root0 - The component props
 * @param root0._columnId - Unique identifier for this column (prefixed with underscore for internal use)
 * @param root0.title - Column header title text displayed to the user
 * @param root0.count - Total number of players in column displayed in badge
 * @param root0.players - Array of sorted Player objects to render in the column
 * @param root0.isEmpty - Boolean flag indicating whether column has no players
 * @param root0.emptyText - Text message displayed when column is empty
 * @param root0.boardId - Optional board identifier for board-specific operations
 * @param root0.boardRefreshTrigger - Optional numeric trigger value for board refresh coordination
 * @param root0.isEditingTitle - Boolean flag indicating whether title is currently being edited
 * @param root0.editTitleValue - Current value of title input field during editing
 * @param root0.isGloballyDragging - Boolean flag indicating whether any drag operation is active across board, defaults to false
 * @param root0.isDraggingFromThisColumn - Boolean flag indicating whether drag operation originated from this specific column
 * @param root0.isDragOver - SE-6554: whether the dragged card is aimed at this lane (drives the drag-over outline)
 * @param root0.onDragOverChange - SE-6554: receives the droppable's `isOver` from the content area below the header
 * @param root0.sortBy - Current sort criterion being applied to players (name, age, gpr, contract, or rank)
 * @param root0.sortDirection - Current sort direction (asc or desc)
 * @param root0.canEdit - Boolean flag indicating whether user can edit column title
 * @param root0.canDelete - Boolean flag indicating whether user can delete the column
 * @param root0.isRankReorderEnabled - SE-4685: whether Rank-sort within-column drag-reorder is active for this column
 * @param root0.onPlayerClick - Callback function invoked when player card is clicked, receives player ID string
 * @param root0.onStartTitleEdit - Callback function invoked when title edit mode should be activated
 * @param root0.onSaveTitleEdit - Callback function invoked when edited title should be saved
 * @param root0.onCancelTitleEdit - Callback function invoked when title editing should be cancelled
 * @param root0.onTitleChange - Callback function invoked when title input value changes, receives new value string
 * @param root0.onPlayerDrop - Callback function invoked when player is dropped into column, receives player data object (typed as unknown for cross-component compatibility)
 * @param root0.onLocalDragStart - Callback function invoked when drag starts from this column, receives dragged data and event (typed as unknown for cross-component compatibility)
 * @param root0.onLocalDragEnd - Callback function invoked when drag from this column ends
 * @param root0.onAutoScroll - Callback function invoked to trigger horizontal auto-scrolling, receives drag X coordinate
 * @param root0.onSort - Callback function invoked when sort option is selected, receives SortOption value
 * @param root0.onDelete - Callback function invoked when column deletion is requested
 * @param root0._onTagAdded - Callback function for tag addition (prefixed with underscore for internal use), receives player ID and tag string
 * @param root0._getPlayerTags - Callback function to retrieve player tags (prefixed with underscore for internal use), receives player ID and returns tag array
 * @param root0.onBoardRefresh - Callback function invoked when board refresh is requested
 */
const KanbanColumnView = ({
  _columnId,
  title,
  count,
  players,
  isEmpty,
  emptyText,
  boardId,
  boardRefreshTrigger,
  isEditingTitle,
  editTitleValue,
  isGloballyDragging = false,
  isDraggingFromThisColumn,
  isDragOver = false,
  onDragOverChange,
  sortBy,
  sortDirection,
  canEdit,
  canDelete,
  isRankReorderEnabled,
  onPlayerClick,
  onStartTitleEdit,
  onSaveTitleEdit,
  onCancelTitleEdit,
  onTitleChange,
  onPlayerDrop,
  onLocalDragStart,
  onLocalDragEnd,
  onAutoScroll,
  onSort,
  onDelete,
  _onTagAdded,
  _getPlayerTags,
  onBoardRefresh,
}: KanbanColumnViewProps) => (
  <Stack
    {...{ "data-testid": TEST_ID_PLAYER_KANBAN.KANBAN_COLUMN }}
    /* Fixed width for horizontal scrolling; z-auto + overflow-visible let drag
       overlays float above without clipping */
    /* `bg-surface-raised` alone — it already carries BOTH themes (#F6F6F6
       light, #272625 dark, which is exactly the artboard's lane colour). The
       `dark:bg-black` that used to follow it overrode that with #000000, the
       same colour as the page behind the board: the lane lost its edges and the
       cards inside it ended up LIGHTER than their own container, inverting the
       depth order light mode has. That flattened, uniform grey is the defect. */
    /*
     * SE-6944 — the no-shrink behaviour below is load-bearing, not decoration.
     * Without it the lane is COMPRESSED below its own content and the cards
     * paint outside it.
     *
     * The mechanism, measured on staging (12 cards, 1920×1080): the board's
     * horizontal ScrollView pins its content container to a percentage minimum
     * height (MainView — it compensates for the container's scale transform).
     * That container resolves to 1098.75px tall, and the per-column sortable
     * wrapper stretches to exactly that. This lane is a flex item in that
     * wrapper's COLUMN axis, so its height is the wrapper's MAIN axis — item
     * alignment never touches it and only the flex factors do.
     *
     * A Stack is a gluestack VStack, and the web VStack base permits shrinking
     * and zeroes its automatic minimum size — unlike the web Box base, which
     * pins shrink off for precisely this reason (see the comment in
     * `components/ui/box/styles.tsx`, which notes that otherwise containers
     * collapse below content height). So the lane could shrink with no floor,
     * and a lane needing 1956px was squeezed into 1098.75px. The cards keep
     * their natural height, the lane does not clip by design (drag overlays),
     * and so 6 of 12 cards rendered outside it — up to 686.6px below.
     *
     * Measured after this change, same board and viewport: lane 1956px, zero
     * cards escaping, and the board's scroll height grows with it so the last
     * card stays reachable. It is a no-op on native, where Yoga already defaults
     * RN views to no shrink — this only corrects the web default.
     *
     * Do NOT "simplify" by dropping it, and do NOT make the lane clip instead:
     * the drag overlay that floats above it would get cut off.
     */
    className={`z-auto flex w-72 min-w-72 shrink-0 flex-col overflow-visible rounded-lg border border-outline-default bg-surface-raised ${
      /* The drag-over ring below is absolutely positioned against this box, so
         the lane has to be a containing block while it is the drop target. */
      isDraggingFromThisColumn || isDragOver ? "relative" : "static"
    } ${
      isGloballyDragging && !isDraggingFromThisColumn
        ? "pointer-events-none"
        : "pointer-events-auto"
    }`}
  >
    {/*
     * SE-6489 — header laid out to the approved design (Figma `Column-Pipeline`,
     * component 80:14448): stage name + count pill on the left, a single
     * right-aligned "..." overflow menu on the right. What used to live here —
     * an inline pencil, an inline red trash, and a standalone sort trigger — are
     * all rows inside that menu now; nothing was removed but the non-interactive
     * "↕" glyph, which the design does not have and which had nothing to relocate.
     */}
    <Box className="rounded-t-lg bg-surface-raised px-3 pb-2 pt-4">
      <Stack direction="horizontal" className="items-center justify-between">
        {/*
         * `space` (not a `gap-*` className) is what actually spaces a Stack —
         * the atom maps the ratified token to the gap class itself, and a
         * hand-written `gap-2` here rendered as 0px.
         */}
        <Stack
          direction="horizontal"
          space="2"
          className="min-w-0 flex-1 items-center"
        >
          {/*
           * Stage status dot — 8px amber circle the design puts before every
           * stage name, populated and empty alike (`Title slot` > `Ellipse`,
           * 8×8, 6px before the label). Decorative only: stages carry no
           * per-stage colour in the data model, so this is a constant accent
           * rather than a data-driven swatch.
           */}
          <Box className="size-2 shrink-0 rounded-full bg-status-warning" />

          <KanbanColumnHeader
            title={title}
            isEditingTitle={isEditingTitle}
            editTitleValue={editTitleValue}
            onSaveTitleEdit={onSaveTitleEdit}
            onCancelTitleEdit={onCancelTitleEdit}
            onTitleChange={onTitleChange}
          />

          {/*
           * One neutral count pill in BOTH states. The design's Count Badge is
           * `var(--background/background200)` on the populated column
           * (80:15028) and on the empty one (80:15030) alike — dev's blue
           * filled badge on populated columns was the divergence.
           *
           * `surface-muted` IS background200; the previous `surface-strong`
           * (background300) contradicted the comment above it. The label is
           * 12px regular per the design, not semibold.
           *
           * SE-6589 extracted the pill this comment describes into the shared
           * CountBadge molecule, unchanged. It lived inline here, which meant a
           * Code Connect template had no real importable component to name —
           * the repo-side half of that ticket's Count Badge blocker.
           */}
          <CountBadge
            count={count}
            testID={TEST_ID_PLAYER_KANBAN.COLUMN_COUNT_BADGE}
          />
        </Stack>

        <StageOptionsMenu
          canEdit={canEdit}
          canDelete={canDelete}
          isEmpty={isEmpty}
          boardId={boardId}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onRename={onStartTitleEdit}
          onSort={onSort}
          onDelete={onDelete}
        />
      </Stack>
    </Box>

    <KanbanColumnContent
      columnId={_columnId}
      players={players}
      isEmpty={isEmpty}
      emptyText={emptyText}
      boardId={boardId}
      boardRefreshTrigger={boardRefreshTrigger}
      isGloballyDragging={isGloballyDragging}
      isDraggingFromThisColumn={isDraggingFromThisColumn}
      onDragOverChange={onDragOverChange}
      canEdit={canEdit}
      isRankReorderEnabled={isRankReorderEnabled}
      onPlayerClick={onPlayerClick}
      onPlayerDrop={onPlayerDrop}
      onLocalDragStart={onLocalDragStart}
      onLocalDragEnd={onLocalDragEnd}
      onAutoScroll={onAutoScroll}
      _onTagAdded={_onTagAdded}
      _getPlayerTags={_getPlayerTags}
      onBoardRefresh={onBoardRefresh}
    />

    {/*
     * SE-6554 (Figma 80:16392, "Column · Drag-over (valid target)") — the lane
     * the card is aimed at outlines itself: 2px dashed CTA, inset 2px, around
     * the WHOLE column including the stage name. The design's own words for the
     * rest of it are "Column bg unchanged", so this adds no tint; the outline
     * alone says "this stage will receive the card".
     *
     * Absolutely positioned, so it costs no layout: nothing inside the lane
     * shifts by a pixel when the card arrives or leaves. That is the same
     * property the constant 2px transparent border inside the content area was
     * introduced for, and it is why the outline is a separate element rather
     * than a border on the column — a border would change the column's box and
     * reflow every card in it on every lane crossing.
     *
     * Rendered last purely for paint order (an absolute child takes no space in
     * the flex flow, so its position among the siblings changes nothing else).
     */}
    {isDragOver ? (
      <Box
        testID={TEST_ID_PLAYER_KANBAN.COLUMN_DRAG_OVER_RING}
        className="absolute inset-micro z-10 rounded-lg border-2 border-dashed border-accent-primary pointer-events-none"
      />
    ) : null}
  </Stack>
);

KanbanColumnView.displayName = "KanbanColumnView";

export default memo(KanbanColumnView);
